import Anser from "anser";

/**
 * Parse an ANSI-escaped string into styled segments ready for React rendering.
 *
 * Returns an array of `{ text, className }` parts — no `dangerouslySetInnerHTML`
 * needed. Uses Tailwind color classes mapped from ANSI codes so the output
 * inherits the current theme (light/dark).
 */
export interface AnsiSegment {
  text: string;
  fg?: string;
  bg?: string;
  decoration?: string;
}

const FG_CLASSES: Record<string, string> = {
  "ansi-black-fg": "text-zinc-900 dark:text-zinc-100",
  "ansi-red-fg": "text-red-500",
  "ansi-green-fg": "text-green-500",
  "ansi-yellow-fg": "text-yellow-500",
  "ansi-blue-fg": "text-blue-500",
  "ansi-magenta-fg": "text-fuchsia-500",
  "ansi-cyan-fg": "text-cyan-500",
  "ansi-white-fg": "text-zinc-200",
  "ansi-bright-black-fg": "text-zinc-500",
  "ansi-bright-red-fg": "text-red-400",
  "ansi-bright-green-fg": "text-green-400",
  "ansi-bright-yellow-fg": "text-yellow-400",
  "ansi-bright-blue-fg": "text-blue-400",
  "ansi-bright-magenta-fg": "text-fuchsia-400",
  "ansi-bright-cyan-fg": "text-cyan-400",
  "ansi-bright-white-fg": "text-zinc-100",
};

const BG_CLASSES: Record<string, string> = {
  "ansi-red-bg": "bg-red-500/20",
  "ansi-green-bg": "bg-green-500/20",
  "ansi-yellow-bg": "bg-yellow-500/20",
  "ansi-blue-bg": "bg-blue-500/20",
  "ansi-magenta-bg": "bg-fuchsia-500/20",
  "ansi-cyan-bg": "bg-cyan-500/20",
};

const DECORATION_CLASSES: Record<string, string> = {
  "ansi-bold": "font-bold",
  "ansi-italic": "italic",
  "ansi-underline": "underline",
};

function anserClassToTailwind(cls: string): string {
  return [
    ...cls.split(" ").map((c) => FG_CLASSES[c]).filter(Boolean),
    ...cls.split(" ").map((c) => BG_CLASSES[c]).filter(Boolean),
    ...cls.split(" ").map((c) => DECORATION_CLASSES[c]).filter(Boolean),
  ].join(" ");
}

/**
 * Parse a log message into plain-text segments with Tailwind class hints.
 * Returns `[{text: fullMessage}]` when the input has no ANSI codes.
 */
export function parseAnsi(input: string): Array<{ text: string; className?: string }> {
  if (!input) return [{ text: "" }];
  if (!input.includes("\x1b")) return [{ text: input }];

  const parsed = Anser.ansiToJson(input, { use_classes: true, remove_empty: true });
  return parsed.map((chunk) => {
    const className = anserClassToTailwind(
      [chunk.fg ? `ansi-${chunk.fg}-fg` : "", chunk.bg ? `ansi-${chunk.bg}-bg` : "", ...(chunk.decorations ?? []).map((d) => `ansi-${d}`)]
        .filter(Boolean)
        .join(" ")
    );
    return { text: chunk.content, className: className || undefined };
  });
}

/**
 * Strip ANSI escapes from a string for plain-text use cases like copy-to-
 * clipboard or server-side search matching.
 */
const ANSI_RE = /\u001b\[[0-9;]*[A-Za-z]/g;

export function stripAnsi(input: string): string {
  if (!input || !input.includes("\u001b")) return input;
  return input.replace(ANSI_RE, "");
}
